import { useState } from "react";
import type { CatalogDraft, Color, ItemCategory } from "../../api/types";
import { DraftReviewRow, draftCanPromote, type DraftRowPatch } from "./DraftReviewRow";

/**
 * G-58 — the review pass: one row per draft (supplier thumbnail · code ·
 * name · category · price once received · barcode), inline edits that save
 * as you go, and "Promote all with suggested category" for the whole
 * supplier block. The detailed card (photo, colour, dimensions, groups)
 * stays available from the page's view switch; this table is for the
 * 113-line delivery note. G-129: the row itself lives in `DraftReviewRow`,
 * shared with the paper page.
 */
interface Props {
  drafts: CatalogDraft[];
  categories: ItemCategory[];
  colors: Color[];
  busyIds: Set<string>;
  onPatch: (draft: CatalogDraft, patch: DraftRowPatch) => Promise<void>;
  onPromoteOne: (draft: CatalogDraft) => Promise<void>;
  onPromoteAll: (drafts: CatalogDraft[]) => Promise<void>;
  onDelete: (draft: CatalogDraft) => Promise<void>;
  /** Saves a photo taken or chosen for one draft. */
  onPhoto: (draftId: string, file: File | null) => Promise<void> | void;
}

export function DraftReviewTable({
  drafts,
  categories,
  colors,
  busyIds,
  onPatch,
  onPhoto,
  onPromoteOne,
  onPromoteAll,
  onDelete,
}: Props) {
  const ready = drafts.filter((d) => draftCanPromote(d, categories));
  const [promotingAll, setPromotingAll] = useState(false);

  return (
    <div className="draft-review">
      <div className="draft-review__toolbar">
        <p className="muted draft-review__note">
          {ready.length} of {drafts.length} rows have a category (chosen or suggested) · prices are
          suggested prices (invoice cost, target margin, IVA)
        </p>
        <button
          type="button"
          className="button"
          disabled={promotingAll || ready.length === 0}
          onClick={() => {
            setPromotingAll(true);
            void onPromoteAll(ready).finally(() => setPromotingAll(false));
          }}
        >
          {promotingAll
            ? "Promoting…"
            : `Promote all with suggested category (${ready.length})`}
        </button>
      </div>
      <div className="table-scroll">
        <table className="draft-review__table">
          <thead>
            <tr>
              <th aria-label="Supplier photo" />
              <th>Supplier code</th>
              <th>Name</th>
              <th>Category</th>
              <th>Colour</th>
              <th>Price once received</th>
              <th>Barcode</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {drafts.map((draft) => {
              const busy = busyIds.has(draft.id);
              const canPromote = draftCanPromote(draft, categories);
              return (
                <DraftReviewRow
                  key={draft.id}
                  draft={draft}
                  categories={categories}
                  colors={colors}
                  busy={busy}
                  onPatch={onPatch}
                  onPhoto={onPhoto}
                  actions={
                    <>
                      <button
                        type="button"
                        className="button"
                        disabled={busy || !canPromote}
                        title={canPromote ? undefined : "Pick a category first"}
                        onClick={() => void onPromoteOne(draft)}
                      >
                        {busy ? "Working…" : "Promote"}
                      </button>
                      <button
                        type="button"
                        className="button danger"
                        disabled={busy}
                        onClick={() => void onDelete(draft)}
                      >
                        Delete
                      </button>
                    </>
                  }
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
